import { type ViolationType } from '@aysar/contracts';
import { useMemo } from 'react';

import { type SelectionOption } from '@/shared/components/selection/types/selection-option.types';
import {
  type ServerSelectQueryArgs,
  type ServerSelectQueryResult,
} from '@/shared/components/selection/types/server-selection.types';

import { useListViolationTypesQuery } from '../../services/violations/violations.api';

/**
 * How a rule is labelled in a picker.
 *
 * The key travels as the description rather than the label: the name is what a
 * staff member recognises, and the key is what they can quote from a case they
 * are looking at — so both are on screen, and neither has to be guessed.
 */
function toOption(type: ViolationType, value: string): SelectionOption {
  return { value, label: type.name, description: type.key };
}

/**
 * The **active** rules, searched on the server (W6).
 *
 * `isActive: 'true'` is a `'true'|'false'` STRING (`BooleanParamSchema`), never a
 * real boolean — sending one is a 422 (N7). Inactive rules are excluded because
 * neither caller can use one: the classifier never matches a retired rule, and
 * filtering the queue by one would ask for rows that can no longer be created.
 *
 * `by` decides which identity the picker deals in, and the two callers genuinely
 * need different ones: the queue filter sends `typeKey` to `GET /violations`,
 * while the classify dialog sends `violationTypeId` to `POST /enforce`.
 */
function useActiveTypeOptions(
  args: ServerSelectQueryArgs,
  by: 'id' | 'key',
): ServerSelectQueryResult {
  const query = useListViolationTypesQuery({
    // An empty term is ABSENT, not `''`: `search` is `.min(1)`, so an empty
    // string is a 422 on the very first render of the picker.
    search: args.search === '' ? undefined : args.search,
    page: args.page,
    pageSize: args.pageSize,
    isActive: 'true',
  });

  const items = useMemo(
    () => (query.data?.items ?? []).map((type) => toOption(type, by === 'id' ? type.id : type.key)),
    [query.data, by],
  );

  return {
    items,
    hasNext: query.data?.meta.hasNext ?? false,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}

/** The queue filter's picker: the value is the rule's **key**. */
export function useViolationTypeKeyOptions(args: ServerSelectQueryArgs): ServerSelectQueryResult {
  return useActiveTypeOptions(args, 'key');
}

/** The classify dialog's picker: the value is the rule's **id**. */
export function useViolationTypeIdOptions(args: ServerSelectQueryArgs): ServerSelectQueryResult {
  return useActiveTypeOptions(args, 'id');
}

/**
 * Resolves a key that arrived in the URL rather than from the panel.
 *
 * Without this, a shared `?typeKey=harassment_abuse` link would open showing the
 * raw key until the operator searched for it — a filtered view an operator can
 * bookmark and paste to a colleague is the whole point of `useListUrlState`, so
 * it has to survive the paste.
 *
 * It searches rather than fetching by id because the catalogue's list query
 * declares no `ids` filter — and the api's `search` is on `key` exactly, so the
 * exact-match narrowing below is a filter over a handful of rows, not a scan.
 */
export function useResolveViolationTypeKeys(values: readonly string[]): {
  items: readonly SelectionOption[];
  isFetching: boolean;
} {
  const key = values[0];
  const query = useListViolationTypesQuery(
    { search: key, pageSize: 20 },
    { skip: key === undefined },
  );

  const items = useMemo(() => {
    if (key === undefined) {
      return [];
    }

    return (query.data?.items ?? [])
      .filter((type) => type.key === key)
      .map((type) => toOption(type, type.key));
  }, [query.data, key]);

  return { items, isFetching: query.isFetching };
}
